import React from "react";
import { Select, type SelectProps } from "antd";
import { ProFormSelect, type ProFormSelectProps } from "@ant-design/pro-components";
import { useTranslation } from "react-i18next";
import {
  countryOptions,
  ensureCountriesLoaded,
  filterCountryOption,
  isValidCountryCode,
  type CountryOption,
} from "../countries/countryUtils";

const SEARCH_PROPS = {
  showSearch: true,
  optionFilterProp: "label" as const,
  filterOption: filterCountryOption,
};

export type CountrySelectProps = Omit<SelectProps, "options" | "showSearch" | "filterOption"> & {
  options?: CountryOption[];
};

export function CountrySelect({ options: optionsProp, ...rest }: CountrySelectProps) {
  const { i18n } = useTranslation();
  const [loadedOptions, setLoadedOptions] = React.useState<CountryOption[] | undefined>(
    optionsProp
  );

  React.useEffect(() => {
    if (optionsProp) {
      setLoadedOptions(optionsProp);
      return;
    }
    let cancelled = false;
    ensureCountriesLoaded()
      .then(() => {
        if (!cancelled) setLoadedOptions(countryOptions(i18n.language));
      })
      .catch(() => {
        if (!cancelled) setLoadedOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [optionsProp, i18n.language]);

  const options = loadedOptions ?? [];

  return (
    <Select
      {...SEARCH_PROPS}
      {...rest}
      options={options}
    />
  );
}

export type ProFormCountrySelectProps = Omit<ProFormSelectProps, "options" | "showSearch" | "filterOption">;

export function ProFormCountrySelect({ fieldProps, rules, ...rest }: ProFormCountrySelectProps) {
  const { i18n } = useTranslation();
  const [options, setOptions] = React.useState<CountryOption[]>([]);

  React.useEffect(() => {
    let cancelled = false;
    ensureCountriesLoaded()
      .then(() => {
        if (!cancelled) setOptions(countryOptions(i18n.language));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  const mergedRules = [
    ...(Array.isArray(rules) ? rules : rules ? [rules] : []),
    {
      validator: async (_: unknown, value: string | undefined) => {
        if (value == null || value === "") return;
        if (!isValidCountryCode(value)) {
          throw new Error("Invalid country code");
        }
      },
    },
  ];

  return (
    <ProFormSelect
      {...rest}
      rules={mergedRules}
      options={options}
      fieldProps={{
        ...SEARCH_PROPS,
        ...fieldProps,
      }}
    />
  );
}
